import { baseApi } from '@klp/core/api/services/rtkApi';
import type { FileObject } from '@klp/file/classes';
import { generatePath } from '@klp/routing';

import { messageApiRoutes } from './api';
import { ByggKunde, Message } from './classes';
import type {
    MessageRequest,
    MessageResponse,
    ByggKundeRequest,
    ByggKundeResponse,
    MessageBoardSummaryRequest,
    MessageStatus,
    MessageListApiResponse,
    ByggKundeApiResponse,
    IMessageInput,
} from './classes';

export const messageboardApi = baseApi.injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
        // Get message list
        getMessageList: builder.query<MessageResponse, MessageRequest>({
            query: (params) => ({
                url: messageApiRoutes.list(),
                params,
            }),
            transformResponse: (response: MessageListApiResponse) => {
                return {
                    messageList: response.map((message) =>
                        Message.parseObject(message),
                    ),
                };
            },
            providesTags: (_result, _error, { kundeNummer }) => [
                { type: 'MessageList', id: kundeNummer },
            ],
        }),

        // Get message board summary (for front page feed)
        getMessageBoardSummary: builder.query<
            MessageResponse,
            MessageBoardSummaryRequest
        >({
            query: (params) => ({
                url: messageApiRoutes.boardSummary(),
                params,
            }),
            transformResponse: (response: MessageListApiResponse) => {
                return {
                    messageList: response.map((message) =>
                        Message.parseObject(message),
                    ),
                };
            },
            providesTags: ['MessageBoardSummary'],
        }),

        // Get single message
        getMessage: builder.query<MessageResponse, MessageRequest>({
            query: (params) => ({
                url: messageApiRoutes.single(params.id!),
                params,
            }),
            transformResponse: (response: IMessageInput) => {
                return {
                    message: Message.parseObject(response),
                };
            },
            providesTags: (_result, _error, { id }) => [
                { type: 'Message', id },
            ],
        }),

        // Post new/updated message
        postMessage: builder.mutation<MessageResponse, Message>({
            queryFn: async (message, _api, _extraOptions, baseQuery) => {
                try {
                    // Post the message
                    const messageData = { ...message.dataModel };
                    const result = await baseQuery({
                        url: messageApiRoutes.list(),
                        method: 'POST',
                        body: messageData,
                    });

                    if (result.error) {
                        throw result.error;
                    }

                    const insertedMessage = Message.parseObject(
                        result.data as IMessageInput,
                    );
                    const { messageFiles } = message;

                    // Delete files
                    const filesToDelete = Object.values<FileObject>(
                        messageFiles,
                    ).filter((file: FileObject) => file.toDelete);

                    if (filesToDelete.length > 0) {
                        await Promise.all(
                            filesToDelete.map((file) =>
                                baseQuery({
                                    url: generatePath(
                                        messageApiRoutes.messageFile(
                                            insertedMessage?.id,
                                        ),
                                        file.id,
                                    ),
                                    method: 'DELETE',
                                }),
                            ),
                        );
                    }

                    // Upload files
                    const filesToUpload = Object.values<FileObject>(
                        messageFiles,
                    ).filter((file: FileObject) => file.local);

                    if (filesToUpload.length > 0) {
                        await Promise.all(
                            filesToUpload.map((file) =>
                                baseQuery({
                                    url: messageApiRoutes.messageFile(
                                        insertedMessage?.id,
                                    ),
                                    method: 'POST',
                                    body: {
                                        filePath: file.fileName,
                                        fileContent:
                                            file.fileContent?.split(',')[1],
                                        type: file.type,
                                    },
                                }),
                            ),
                        );
                    }

                    return {
                        data: {
                            message: insertedMessage,
                        },
                    };
                } catch (error) {
                    return { error };
                }
            },
            invalidatesTags: (_result, _error, message) => [
                'MessageList',
                'MessageBoardSummary',
                { type: 'Message', id: message.id },
            ],
        }),

        // Update message status
        updateMessageStatus: builder.mutation<
            MessageResponse,
            { id: number; status: MessageStatus; kundeNummer: string }
        >({
            queryFn: async (
                { id, status, kundeNummer },
                _api,
                _extraOptions,
                baseQuery,
            ) => {
                try {
                    const result = await baseQuery({
                        url: messageApiRoutes.status(id, status),
                        method: 'PUT',
                        body: { kundeNummer },
                    });

                    if (result.error) {
                        throw result.error;
                    }

                    return {
                        data: {
                            message: Message.parseObject(
                                result.data as IMessageInput,
                            ),
                        },
                    };
                } catch (error) {
                    return { error };
                }
            },
            invalidatesTags: (_result, _error, { id, kundeNummer }) => [
                { type: 'Message', id },
                { type: 'MessageList', id: kundeNummer },
                'MessageBoardSummary',
            ],
        }),

        // Get building and customer (ByggKunde)
        getByggKunde: builder.query<ByggKundeResponse, ByggKundeRequest>({
            query: (params) => ({
                url: messageApiRoutes.buildingAndCustomer(),
                params,
            }),
            transformResponse: (response: ByggKundeApiResponse) => {
                return {
                    byggKunde: response.map((row) => new ByggKunde(row)),
                };
            },
            providesTags: ['ByggKunde'],
        }),
    }),
});

export const {
    useGetMessageListQuery,
    useLazyGetMessageListQuery,
    useGetMessageBoardSummaryQuery,
    useGetMessageQuery,
    useLazyGetMessageQuery,
    usePostMessageMutation,
    useUpdateMessageStatusMutation,
    useGetByggKundeQuery,
    useLazyGetByggKundeQuery,
} = messageboardApi;
